/**
 * OptionsPollingSettings (R126) — full UI control for the option-chain poller.
 *
 * Lets the user: turn polling on/off, pick the cadence, bound what's polled
 * (strike window, expiries, max underlyings, retention), curate the
 * Options-Watch set (add / remove / enable / disable / refresh-now), and read
 * an in-app guide explaining every control. All live — no restart.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Card, CardHeader, CardContent, Typography, Switch, FormControlLabel,
  TextField, MenuItem, Button, Alert, Chip, Divider, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Tooltip, IconButton,
  InputAdornment, Accordion, AccordionSummary, AccordionDetails, Autocomplete,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { optionsService } from '../../services/options.service';
import { toast } from 'react-hot-toast';

interface ConfigRow {
  enabled: boolean;
  intervalMinutes: number;
  strikeWindow: number;
  moneynessLow: number;
  moneynessHigh: number;
  expiriesToTrack: number;
  historyRetentionDays: number;
  eodRetentionDays: number;
  maxUnderlyings: number;
  nseDirectEnabled: boolean;
  lastAutoDisableReason?: string | null;
}
interface WatchRow {
  id: number; symbol: string; displayName?: string;
  feedSource: string; isIndex: boolean; isTracked: boolean; optionsWatch: boolean;
}

const DEFAULTS: ConfigRow = {
  enabled: false, intervalMinutes: 5, strikeWindow: 12, moneynessLow: 0.85, moneynessHigh: 1.15,
  expiriesToTrack: 2, historyRetentionDays: 3, eodRetentionDays: 60, maxUnderlyings: 10, nseDirectEnabled: false,
};

const num = (v: any, d: number) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

const OptionsPollingSettings: React.FC = () => {
  const [cfg, setCfg] = useState<ConfigRow>(DEFAULTS);
  const [watch, setWatch] = useState<WatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpts, setSearchOpts] = useState<{ symbol: string; exchange: string }[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState<{ symbol: string; exchange: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([optionsService.getPollingConfig(), optionsService.getPollingWatch()]);
      const cd = (c.data as any)?.data;
      const row = (cd?.config) || (cd?.resolved) || DEFAULTS;
      setCfg({ ...DEFAULTS, ...row });
      setWatch((((w.data as any)?.data) || []) as WatchRow[]);
    } catch {
      toast.error('Failed to load options-polling settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch: Partial<ConfigRow>) => {
    setSaving(true);
    setCfg(prev => ({ ...prev, ...patch }));
    try {
      const r = await optionsService.updatePollingConfig(patch);
      const saved = (r.data as any)?.data;
      if (saved) setCfg({ ...DEFAULTS, ...saved });
      toast.success('Options polling updated');
    } catch {
      toast.error('Save failed'); load();
    } finally { setSaving(false); }
  };

  const toggleWatch = async (row: WatchRow) => {
    const nextVal = !row.optionsWatch;
    setWatch(ws => ws.map(x => x.id === row.id ? { ...x, optionsWatch: nextVal } : x));
    try { await optionsService.setPollingWatch(row.id, nextVal); }
    catch { toast.error(`Failed to update ${row.symbol}`); load(); }
  };

  // Debounced instrument search powering the add-autocomplete (same /instruments
  // source the Charts symbol panel uses). Filtered to underlyings — drops the
  // individual CE/PE/FUT contracts and non-option exchanges.
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) { setSearchOpts([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await optionsService.searchInstruments(term.toUpperCase());
        const items: any[] = (r.data as any)?.data?.instruments || (r.data as any)?.instruments || [];
        const KEEP = new Set(['NSE', 'BSE', 'DELTA', 'MCX']);
        const seen = new Set<string>();
        const opts: { symbol: string; exchange: string }[] = [];
        for (const i of items) {
          const exch = (i.exchange || '').toUpperCase();
          const type = (i.instrumentType || '').toUpperCase();
          if (!KEEP.has(exch) || ['CE', 'PE', 'FUT'].includes(type)) continue;
          const key = `${i.symbol}|${exch}`;
          if (seen.has(key)) continue;
          seen.add(key);
          opts.push({ symbol: i.symbol, exchange: exch });
        }
        setSearchOpts(opts);
      } catch { setSearchOpts([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const addSelected = async () => {
    if (!selectedOpt) return;
    setAdding(true);
    try {
      const r = await optionsService.addUnderlying({ symbol: selectedOpt.symbol, exchange: selectedOpt.exchange });
      const created = (r.data as any)?.data;
      if (created?.id) await optionsService.setPollingWatch(created.id, true);
      toast.success(`Added ${selectedOpt.symbol} (${selectedOpt.exchange}) — polling enabled`);
      setSelectedOpt(null); setSearchTerm(''); setSearchOpts([]);
      await load();
    } catch {
      toast.error(`Could not add ${selectedOpt.symbol}`);
    } finally { setAdding(false); }
  };

  const refreshOne = async (row: WatchRow) => {
    setRefreshingId(row.id);
    try { await optionsService.refreshSymbol(row.symbol); toast.success(`Refresh triggered for ${row.symbol}`); }
    catch { toast.error(`Refresh failed for ${row.symbol}`); }
    finally { setTimeout(() => setRefreshingId(null), 1200); }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toUpperCase();
    const rows = q ? watch.filter(w => w.symbol.includes(q) || (w.displayName || '').toUpperCase().includes(q)) : watch;
    // Active (polled) first, then alphabetical.
    return [...rows].sort((a, b) => (Number(b.optionsWatch) - Number(a.optionsWatch)) || a.symbol.localeCompare(b.symbol));
  }, [watch, filter]);

  const watchCount = watch.filter(w => w.optionsWatch).length;
  const overCap = watchCount > cfg.maxUnderlyings;

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  return (
    <Card sx={{ borderLeft: '4px solid #7e57c2' }}>
      <CardHeader
        avatar={<BoltIcon sx={{ color: '#7e57c2' }} />}
        title={<Typography variant="h6" sx={{ fontWeight: 700 }}>Options Polling</Typography>}
        subheader="Fetch live option-chain data (OI, IV, Greeks, PCR, max-pain) for your chosen underlyings — bounded and safe. Drives the FNO / Options Dashboard, the Options Lab and the chart FNO overlays."
      />
      <CardContent>
        {cfg.lastAutoDisableReason && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Auto-disabled by the safety circuit breaker: <strong>{cfg.lastAutoDisableReason}</strong>. Re-enable below once resolved.
          </Alert>
        )}

        {/* Master switch + Emergency stop */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} disabled={saving} color="success" />}
            label={<Typography sx={{ fontWeight: 700 }}>{cfg.enabled ? 'Polling ON' : 'Polling OFF'}</Typography>}
          />
          {cfg.enabled && (
            <Button size="small" color="error" variant="outlined" disabled={saving} onClick={() => save({ enabled: false })}>
              Emergency Stop
            </Button>
          )}
          {saving && <CircularProgress size={16} />}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Tuning grid — all uniform TextFields for aligned look */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 2, alignItems: 'start', mb: 2 }}>
          <TextField select fullWidth size="small" label="Poll interval" value={cfg.intervalMinutes} disabled={saving}
            onChange={(e) => save({ intervalMinutes: Number(e.target.value) })}>
            <MenuItem value={3}>Every 3 minutes</MenuItem>
            <MenuItem value={5}>Every 5 minutes</MenuItem>
            <MenuItem value={10}>Every 10 minutes</MenuItem>
            <MenuItem value={15}>Every 15 minutes</MenuItem>
          </TextField>
          <TextField select fullWidth size="small" label="Expiries" value={cfg.expiriesToTrack} disabled={saving}
            onChange={(e) => save({ expiriesToTrack: Number(e.target.value) })}>
            <MenuItem value={1}>Near only</MenuItem>
            <MenuItem value={2}>Near + next</MenuItem>
          </TextField>
          <TextField fullWidth label="Strike window (ATM ±)" type="number" size="small" value={cfg.strikeWindow} disabled={saving}
            onChange={(e) => setCfg({ ...cfg, strikeWindow: num(e.target.value, cfg.strikeWindow) })}
            onBlur={() => save({ strikeWindow: cfg.strikeWindow })} inputProps={{ min: 3, max: 50 }} />
          <TextField fullWidth label="Max underlyings" type="number" size="small" value={cfg.maxUnderlyings} disabled={saving}
            onChange={(e) => setCfg({ ...cfg, maxUnderlyings: num(e.target.value, cfg.maxUnderlyings) })}
            onBlur={() => save({ maxUnderlyings: cfg.maxUnderlyings })} inputProps={{ min: 1, max: 25 }} />
          <TextField fullWidth label="Intraday history (days)" type="number" size="small" value={cfg.historyRetentionDays} disabled={saving}
            onChange={(e) => setCfg({ ...cfg, historyRetentionDays: num(e.target.value, cfg.historyRetentionDays) })}
            onBlur={() => save({ historyRetentionDays: cfg.historyRetentionDays })} inputProps={{ min: 1, max: 90 }} />
          <TextField fullWidth label="EOD history (days)" type="number" size="small" value={cfg.eodRetentionDays} disabled={saving}
            onChange={(e) => setCfg({ ...cfg, eodRetentionDays: num(e.target.value, cfg.eodRetentionDays) })}
            onBlur={() => save({ eodRetentionDays: cfg.eodRetentionDays })} inputProps={{ min: 7, max: 730 }} />
        </Box>

        <FormControlLabel
          control={<Switch checked={cfg.nseDirectEnabled} onChange={(e) => save({ nseDirectEnabled: e.target.checked })} disabled={saving} />}
          label={<Tooltip title="The NSE public option-chain API is usually blocked from the server IP. Leave OFF — Zerodha is the primary NSE feed. Only enable if the server IP has been unblocked with NSE."><Typography variant="body2">Try NSE-direct feed (advanced — usually OFF)</Typography></Tooltip>}
        />

        <Divider sx={{ my: 2 }} />

        {/* Options-Watch set */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Options-Watch set</Typography>
          <Chip size="small" label={`${watchCount} polling`} color={overCap ? 'warning' : 'primary'} variant={overCap ? 'filled' : 'outlined'} />
          <Tooltip title="Only the underlyings with Poll = ON are fetched each cycle. If more than the Max-underlyings cap are ON, only the first (by cap) are polled per cycle.">
            <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Tooltip>
        </Box>
        {overCap && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {watchCount} underlyings are ON, above the {cfg.maxUnderlyings}-per-cycle cap. The poller <strong>rotates</strong> through them in
            batches of {cfg.maxUnderlyings} each cycle, so every underlying refreshes about every{' '}
            <strong>{Math.ceil(watchCount / cfg.maxUnderlyings)} cycles (~{Math.ceil(watchCount / cfg.maxUnderlyings) * cfg.intervalMinutes} min)</strong>.
            Raise <strong>Max underlyings</strong> for fresher data, or turn some off.
          </Alert>
        )}

        {/* Add (autocomplete) + filter row */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <Autocomplete
            size="small" sx={{ width: 300 }} disabled={adding}
            options={searchOpts} value={selectedOpt}
            getOptionLabel={(o) => `${o.symbol} · ${o.exchange}`}
            isOptionEqualToValue={(a, b) => a.symbol === b.symbol && a.exchange === b.exchange}
            filterOptions={(x) => x} loading={searchLoading}
            noOptionsText={searchTerm.trim().length < 2 ? 'Type 2+ letters to search…' : 'No matches'}
            onChange={(_, val) => setSelectedOpt(val)}
            onInputChange={(_, val, reason) => { if (reason === 'input') setSearchTerm(val); }}
            renderInput={(params) => (
              <TextField {...params} label="Add underlying — search" placeholder="e.g. RELIANCE, NIFTY, BTCUSD"
                InputProps={{ ...params.InputProps, endAdornment: (<>{searchLoading ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>) }} />
            )}
          />
          <Button size="small" variant="contained" startIcon={adding ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            onClick={addSelected} disabled={adding || !selectedOpt}>Add &amp; poll</Button>
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder="Filter list…" value={filter} onChange={(e) => setFilter(e.target.value)}
            sx={{ width: 180 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        </Box>

        <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Feed</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Poll
                  <Tooltip title="ON = this underlying's option chain is fetched every cycle. OFF = excluded from polling (kept in the list, not deleted).">
                    <HelpOutlineIcon sx={{ fontSize: '0.8rem', ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Refresh</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(row => (
                <TableRow key={row.id} hover sx={{ opacity: row.optionsWatch ? 1 : 0.6 }}>
                  <TableCell>{row.symbol}{row.isIndex ? ' (index)' : ''}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={row.feedSource} /></TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={row.optionsWatch} onChange={() => toggleWatch(row)} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={row.optionsWatch ? 'Fetch this chain now (one-off)' : 'Enable polling first'}>
                      <span>
                        <IconButton size="small" disabled={!row.optionsWatch || refreshingId === row.id} onClick={() => refreshOne(row)}>
                          {refreshingId === row.id ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">{watch.length ? 'No matches.' : 'No option underlyings yet — add one above.'}</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* In-app guide */}
        <Accordion sx={{ mt: 2 }} disableGutters elevation={0} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpOutlineIcon fontSize="small" color="primary" />
              <Typography sx={{ fontWeight: 700 }}>What does each control do? (guide)</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This section controls the <strong>option-chain poller</strong> — the background job that periodically fetches option data
              (Open Interest, IV, Greeks, PCR, max-pain, support/resistance) for the underlyings you choose. That data powers the
              <strong> FNO / Options Dashboard</strong>, the <strong>Options Lab</strong> and the <strong>FNO overlays on Charts</strong>.
              It is bounded and self-protecting so it can never overload the app.
            </Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Control</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>What it does</TableCell>
              </TableRow></TableHead>
              <TableBody>
                <TableRow><TableCell><strong>Polling ON / OFF</strong></TableCell><TableCell>Master switch. OFF = nothing is fetched (the poller idles, zero load). ON = it fetches on your interval.</TableCell></TableRow>
                <TableRow><TableCell><strong>Emergency Stop</strong></TableCell><TableCell>One click to turn polling OFF instantly — use if you ever suspect it's affecting performance.</TableCell></TableRow>
                <TableRow><TableCell><strong>Poll interval</strong></TableCell><TableCell>How often to fetch (3–15 min). 5 min is ideal for swing trading. Lower = fresher but more load.</TableCell></TableRow>
                <TableRow><TableCell><strong>Expiries</strong></TableCell><TableCell>How many expiries to fetch per underlying — <em>Near only</em> or <em>Near + next</em>. Swing setups live here.</TableCell></TableRow>
                <TableRow><TableCell><strong>Strike window (ATM ±)</strong></TableCell><TableCell>How many strikes around at-the-money to fetch on each side. 12 ≈ the liquid, tradeable strikes; deep OTM/ITM are skipped.</TableCell></TableRow>
                <TableRow><TableCell><strong>Max underlyings</strong></TableCell><TableCell>How many watched underlyings are polled <em>per cycle</em> — the main load throttle. If more are switched on than this cap, the poller <strong>rotates</strong> through them in batches, so each one refreshes every ceil(total ÷ cap) cycles (e.g. 200 on with cap 10 = each refreshes every 20 cycles).</TableCell></TableRow>
                <TableRow><TableCell><strong>Intraday history (days)</strong></TableCell><TableCell>How long full per-cycle history is kept before it's downsampled to one end-of-day row per strike (for OI-buildup over the day).</TableCell></TableRow>
                <TableRow><TableCell><strong>EOD history (days)</strong></TableCell><TableCell>How long the daily end-of-day summary per strike is kept (for day-over-day OI trend). Beyond this it's purged.</TableCell></TableRow>
                <TableRow><TableCell><strong>NSE-direct feed</strong></TableCell><TableCell>Advanced. NSE's public API is normally blocked from the server, so leave OFF — Zerodha is the NSE feed. Crypto always uses Delta.</TableCell></TableRow>
                <TableRow><TableCell><strong>Options-Watch set</strong></TableCell><TableCell>The list of underlyings the poller may fetch. <strong>Search &amp; add</strong> any symbol from the universe (pick the symbol·exchange match) to include it; the <strong>Poll</strong> switch on each row turns that underlying on/off without deleting it.</TableCell></TableRow>
                <TableRow><TableCell><strong>Poll switch (per row)</strong></TableCell><TableCell>ON = include this underlying every cycle. OFF = skip it (stays in the list). This is how you enable/disable individual symbols.</TableCell></TableRow>
                <TableRow><TableCell><strong>Refresh (per row)</strong></TableCell><TableCell>Fetch that one chain immediately, once — handy right after adding a symbol or to force a fresh read without waiting for the next cycle.</TableCell></TableRow>
              </TableBody>
            </Table>
            <Alert severity="info" sx={{ mt: 1.5 }}>
              <strong>NSE data needs a live Zerodha login</strong> (Settings → Broker Credentials). Until you're logged in, NSE underlyings
              (NIFTY, BANKNIFTY, stocks) show no data; crypto (Delta) works 24×7 without a daily login.
            </Alert>
          </AccordionDetails>
        </Accordion>

        <Alert severity="info" sx={{ mt: 2 }}>
          The poller self-protects: it skips a cycle under memory pressure or if the previous one is still running, and auto-disables
          after repeated overruns/errors. Storage is kept small by the <strong>Options Data Cleanup</strong> task (Tasks tab).
        </Alert>
      </CardContent>
    </Card>
  );
};

export default OptionsPollingSettings;
